import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import AppApiService from '../../ApiServices/WebsiteApiService/AppApiService';
import { useAuth } from "../Auth/AuthContext"; 
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        padding: '0',
        borderRadius: '8px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
        backgroundColor: '#f9f9f9',
        width: '500px',
    },
};

const AddWebsiteModalForm = ({ isOpen, closeModal }) => {
    const [formData, setFormData] = useState({
        Name: '',
        Url: '',
        Title: '',
        Description: '',
        AppWeb: 'Website',
        UserId: '',
    });
    const [publishers, setPublishers] = useState([]); // Store publishers list
    const [loading, setLoading] = useState(false);

    const { authState } = useAuth(); // Get authState from AuthContext
    const user = authState.user;   
    const role = authState.roles;
       const admin = role === "Admin";

    useEffect(() => {
        if (admin) {
            fetchPublishers();
        }
    }, [admin]);

    const fetchPublishers = async () => {
        try {
            const response = await PublisherApiService.getAllApproved(); // Fetch publishers
            setPublishers(response.data);
        } catch (error) {
            console.error("Error fetching publishers", error);
            toast.error("Failed to load publishers");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
    
        // Get the selected appWeb type safely
        const appWebElement = document.querySelector('input[name="AppWeb"]:checked');
        if (!appWebElement) {
            toast.error("Please select either Website or App");
            setLoading(false);
            return;
        }
        const appWeb = appWebElement.value;
    
        const selectedUserId = admin ? formData.UserId : user.id; // Assign user ID based on role
    
        try {
            let result;
            if (appWeb === "Website") {
                if (!formData.Name || !formData.Url) {
                    toast.error("Name and URL are required for a Website");
                    setLoading(false);
                    return;
                }
    
                const websiteData = {
                    Name: formData.Name,
                    Url: formData.Url,
                    Title: formData.Title || "",
                    Description: formData.Description || "",
                    AppWeb: appWeb,
                    UserId: selectedUserId,
                };
                result = await WebsiteApiService.Add(websiteData);
            } else if (appWeb === "App") {
                if (!formData.AppId || !formData.AppName || !formData.AppUrl) {
                    toast.error("AppId, AppName, and AppUrl are required for an App");
                    setLoading(false);
                    return;
                }
    
                const appData = {
                    AppId: formData.AppId,
                    Name: formData.AppName,
                    Url: formData.AppUrl,
                    Title: formData.Title || "",
                    Description: formData.Description || "",
                    UserId: selectedUserId,
                };
                result = await AppApiService.Add(appData);
            }
    
            // Handle success response
            toast.success(result?.message || "Successfully added");
            closeModal();
            setFormData({
                Name: "",
                Url: "",
                AppId: "",
                AppName: "",
                AppUrl: "",
                Title: "",
                Description: "",
                AppWeb: "Website",
                UserId: "",
            });
        } catch (error) {
            toast.error(error.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <>
            <ToastContainer />
            <Modal isOpen={isOpen} onRequestClose={closeModal} style={customStyles} ariaHideApp={false}>
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-center mb-4 text-gray-700">Website/App Form</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="flex justify-center space-x-4 mb-4">
                            {['Website', 'App'].map((option) => (
                                <label key={option} className="flex items-center">
                                    <input
                                        type="radio"
                                        name="AppWeb"
                                        value={option}
                                        checked={formData.AppWeb === option}
                                        onChange={handleInputChange}
                                        className="form-radio text-blue-500"
                                    />
                                    <span className="ml-2 text-gray-600">{option}</span>
                                </label>
                            ))}
                        </div>

                        {/* Admin-specific dropdown for selecting publishers */}
                        {admin && (
                            <div className="flex flex-col">
                                <label htmlFor="UserId" className="text-gray-600 mb-1">
                                    Select Publisher
                                </label>
                                <select
                                    id="UserId"
                                    name="UserId"
                                    value={formData.UserId}
                                    onChange={handleInputChange}
                                    className="border border-gray-300 rounded-md p-2 focus:outline-none focus:border-blue-500"
                                >
                                    <option value="">Select Publisher</option>
                                    {publishers.map((publisher) => (
                                        <option key={publisher.id} value={publisher.id}>
                                            {publisher.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Website fields */}
                        {formData.AppWeb === 'Website' && (
                            <>
                                {['Name', 'Url'].map((field) => (
                                    <div key={field} className="flex flex-col">
                                        <label htmlFor={field} className="text-gray-600 mb-1">
                                            {field}
                                        </label>
                                        <input
                                            type="text"
                                            id={field}
                                            name={field}
                                            value={formData[field] || ''}
                                            onChange={handleInputChange}
                                            className="border border-gray-300 rounded-md p-2 focus:outline-none focus:border-blue-500"
                                            placeholder={`Enter ${field}`}
                                        />
                                    </div>
                                ))}
                            </>
                        )}

                        {/* App fields */}
                        {formData.AppWeb === 'App' && (
                            <>
                                {['AppId', 'AppName', 'AppUrl'].map((field) => (
                                    <div key={field} className="flex flex-col">
                                        <label htmlFor={field} className="text-gray-600 mb-1">
                                            {field}
                                        </label>
                                        <input
                                            type="text"
                                            id={field}
                                            name={field}
                                            value={formData[field] || ''}
                                            onChange={handleInputChange}
                                            className="border border-gray-300 rounded-md p-2 focus:outline-none focus:border-blue-500"
                                            placeholder={`Enter ${field}`}
                                        />
                                    </div>
                                ))}
                            </>
                        )}

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-md hover:bg-blue-700 transition duration-200"
                                disabled={loading}
                            >
                                {loading ? "Adding..." : "Add App/Web"}
                            </button>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md shadow-md hover:bg-gray-400 transition duration-200"
                            >
                                Close
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </>
    );
};

export default AddWebsiteModalForm;
